import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Moon, Sun, Menu, X } from "lucide-react";
import { SOCIALS } from "../data/mock";

const LINKS = [
  { href: "#show", label: "Podcast", testId: "nav-show-link" },
  { href: "#essays", label: "Essays", testId: "nav-essays-link" },
  { href: "#quotes", label: "Quotes", testId: "nav-quotes-link" },
];

export const Navbar = () => {
  const [dark, setDark] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const stored = localStorage.getItem("theme");
    const hour = new Date().getHours();
    const isDark = stored ? stored === "dark" : hour >= 19 || hour < 6;
    setDark(isDark);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const toggleTheme = () => {
    setDark((d) => {
      localStorage.setItem("theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const to = (hash) => (onHome ? hash : `/${hash}`);

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl"
      style={{
        background: "color-mix(in srgb, var(--brand-cream) 88%, transparent)",
        borderBottom: "1px solid var(--brand-line)",
      }}
    >
      <div className="px-6 md:px-12 lg:px-20 h-16 md:h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="nav-brand-link"
            className="text-lg md:text-xl font-bold tracking-tight hover:opacity-70 transition-opacity duration-300"
            style={{ color: "var(--brand-ink)" }}
          >
            Hunar Syal
          </Link>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label="Toggle day and night mode"
            role="switch"
            aria-checked={dark}
            className="relative inline-flex items-center h-7 w-[52px] rounded-full transition-colors duration-300"
            style={{
              background: dark ? "#0F172A" : "#E2E8F0",
              border: "1px solid var(--brand-line)",
            }}
          >
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 flex items-center">
              <Sun size={11} strokeWidth={2.2} style={{ color: dark ? "#64748B" : "#F59E0B" }} />
            </span>
            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center">
              <Moon size={11} strokeWidth={2.2} style={{ color: dark ? "#F1F5F9" : "#94A3B8" }} />
            </span>
            <span
              className="absolute top-1/2 -translate-y-1/2 w-[22px] h-[22px] rounded-full shadow-sm transition-all duration-300"
              style={{
                left: dark ? "27px" : "3px",
                background: dark ? "#F1F5F9" : "#FFFFFF",
              }}
            />
          </button>
        </div>

        <div
          className="hidden md:flex items-center gap-9 text-[15px] font-semibold"
          style={{ color: "var(--brand-ink-soft)" }}
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={to(l.href)}
              data-testid={l.testId}
              className="hover:opacity-70 transition-opacity duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <a href={SOCIALS.instagramDM} target="_blank" rel="noopener noreferrer" data-testid="nav-instagram-link" aria-label="Follow on Instagram" className="hover:-translate-y-0.5 transition-transform duration-300">
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white"
                style={{
                  background:
                    "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 85%,#515BD4 100%)",
                }}
              >
                <Instagram size={15} strokeWidth={2} />
              </span>
            </a>
            <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" data-testid="nav-youtube-link" aria-label="Subscribe on YouTube" className="hover:-translate-y-0.5 transition-transform duration-300">
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white"
                style={{ background: "#FF0000" }}
              >
                <Youtube size={16} strokeWidth={2} />
              </span>
            </a>
            <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="nav-linkedin-link" aria-label="Connect on LinkedIn" className="hidden sm:inline-flex hover:-translate-y-0.5 transition-transform duration-300">
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white"
                style={{ background: "#0A66C2" }}
              >
                <Linkedin size={15} strokeWidth={2} />
              </span>
            </a>
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="mobile-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-full"
            style={{
              border: "1px solid var(--brand-line)",
              color: "var(--brand-ink-soft)",
            }}
          >
            {menuOpen ? <X size={18} strokeWidth={1.8} /> : <Menu size={18} strokeWidth={1.8} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden px-6 py-5"
          style={{
            background: "var(--brand-cream)",
            borderTop: "1px solid var(--brand-line)",
          }}
        >
          <div
            className="flex flex-col gap-4 text-base font-semibold"
            style={{ color: "var(--brand-ink)" }}
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={to(l.href)}
                data-testid={`mobile-${l.testId}`}
                onClick={() => setMenuOpen(false)}
              >
                {l.label}
              </a>
            ))}
          </div>
          <div
            className="mt-5 pt-4 flex items-center gap-6"
            style={{
              borderTop: "1px solid var(--brand-line)",
              color: "var(--brand-ink-soft)",
            }}
          >
            <a href={SOCIALS.instagramDM} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-instagram-link" aria-label="Instagram">
              <Instagram size={20} strokeWidth={1.8} />
            </a>
            <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-youtube-link" aria-label="YouTube">
              <Youtube size={22} strokeWidth={1.8} />
            </a>
            <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-linkedin-link" aria-label="LinkedIn">
              <Linkedin size={19} strokeWidth={1.8} />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
